import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import { api } from '@/lib/api';
import { Badge, Button, PageHeader, ScoreBadge } from '@/components/ui';
import { formatDateTime, formatPhone, formatService } from '@/lib/format';

interface QualifiedLead {
  id?: string;
  call_id: string;
  lead_score: number;
  services: string[];
  summary: string | null;
  homeowner: boolean | null;
  postal_code: string | null;
  urgency: string | null;
  timeline: string | null;
  created_at: string;
  caller_number: string | null;
  vicidial_campaign: string | null;
  vicidial_user: string | null;
  state: string;
  transferred: boolean;
  disposition: string | null;
  first_name: string | null;
  last_name: string | null;
  bot_name: string | null;
}

export default function QualifiedLeads() {
  const navigate = useNavigate();
  const [query, setQuery] = useState<Record<string, string | number | boolean | null | undefined>>({});
  // Stable, so the effect in DataTable does not re-fire every render.
  const onQuery = useCallback((q: Record<string, string | number | boolean | null | undefined>) => setQuery(q), []);

  return (
    <div className="w-full">
      <PageHeader
        title="Qualified leads"
        description="Conversations the bot judged worth a human specialist."
        actions={
          // The filters this page has, not a bare download. An export that
          // quietly hands back everything is worse than none: somebody who
          // has narrowed the list believes the file is what they narrowed to.
          <Button
            variant="secondary"
            onClick={() => api.download('/reports/qualified-leads.csv', query)}
          >
            Export CSV
          </Button>
        }
      />

      <DataTable<QualifiedLead>
        queryKey="qualified-leads"
        path="/qualified-leads"
        onQuery={onQuery}
        searchable={false}
        onRowClick={(lead) => navigate(`/calls/${lead.call_id}`)}
        emptyTitle="No qualified leads yet"
        emptyDescription="A lead qualifies when every required field is collected AND the score clears the bot's threshold."
        columns={[
          {
            key: 'created_at',
            header: 'When',
            render: (lead) => (
              <span className="whitespace-nowrap text-xs text-ink-500">
                {formatDateTime(lead.created_at)}
              </span>
            ),
          },
          {
            key: 'customer',
            header: 'Customer',
            render: (lead) => (
              <div>
                <div className="font-mono text-sm">{formatPhone(lead.caller_number)}</div>
                <div className="text-xs text-ink-500">
                  {[lead.first_name, lead.last_name].filter(Boolean).join(' ') || '—'}
                </div>
              </div>
            ),
          },
          {
            key: 'services',
            header: 'Services',
            render: (lead) => (
              <div className="flex flex-wrap gap-1">
                {(lead.services ?? []).length === 0 ? (
                  <span className="text-ink-400">—</span>
                ) : (
                  lead.services.map((service) => (
                    <Badge key={service} tone="blue">
                      {formatService(service)}
                    </Badge>
                  ))
                )}
              </div>
            ),
          },
          {
            key: 'score',
            header: 'Score',
            numeric: true,
            render: (lead) => <ScoreBadge score={lead.lead_score} />,
          },
          {
            key: 'homeowner',
            header: 'Homeowner',
            render: (lead) =>
              lead.homeowner === null ? (
                <span className="text-ink-400">—</span>
              ) : (
                <Badge tone={lead.homeowner ? 'green' : 'gray'}>
                  {lead.homeowner ? 'Yes' : 'No'}
                </Badge>
              ),
          },
          {
            key: 'zip',
            header: 'ZIP',
            render: (lead) => lead.postal_code ?? <span className="text-ink-400">—</span>,
          },
          {
            key: 'urgency',
            header: 'Urgency',
            render: (lead) => lead.urgency ?? <span className="text-ink-400">—</span>,
          },
          {
            key: 'outcome',
            header: 'Outcome',
            // `transferred`, not the state. The state does not survive the
            // call -- every finished call is ENDED -- so this column said
            // "ENDED" on every lead that had in fact been handed to a
            // closer, which is the one thing this page exists to show.
            render: (lead) => (
              <Badge tone={lead.transferred ? 'green' : 'amber'}>
                {lead.transferred ? 'Transferred' : lead.state.replace(/_/g, ' ')}
              </Badge>
            ),
          },
        ]}
      />
    </div>
  );
}
